import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styles } from "@repo/helpers";
import { SortTypeEnum } from "@repo/models";
import { MouseEventHandler, useCallback } from "react";

import { Column } from "../models";
import style from "../style.module.scss";

interface SortableColumnProps<T> extends PassThruProps {
	column: Column<T>;
}

export interface PassThruProps {
	columnClassName?: string;
	resizeable?: boolean;
	reorderable?: boolean;
	sortEventHandler?: (id: string) => void;
	setCurrentResizeTh?: MouseEventHandler<HTMLDivElement>;
}

export function SortableColumn<T>(props: SortableColumnProps<T>) {
	const { column, columnClassName, resizeable, reorderable, setCurrentResizeTh } = props;
	const cellClass = styles(style.tableColumn, columnClassName, props.resizeable && style.resizeableHeader);
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: column.id });
	const dropStyle = reorderable ? { transform: CSS.Transform.toString(transform), transition } : {};

	const sortEventHandler = useCallback(() => {
		props.sortEventHandler(column.id);
	}, [props.sortEventHandler, column.id]);

	const resizePointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			e.stopPropagation();
			setCurrentResizeTh(e);
		},
		[setCurrentResizeTh],
	);

	return (
		<th
			className={cellClass}
			key={column.id}
			style={{
				minWidth: column.minWidth || null,
				display: column.visible ? null : "none",
				width: column.width || null,
				...dropStyle,
			}}
			onClick={sortEventHandler}
			ref={setNodeRef}
			{...attributes}
			{...listeners}
		>
			{column.name}
			{sortIcon()}
			{resizer()}
		</th>
	);
	function sortIcon() {
		if (column.sortBy === SortTypeEnum.none) return null;
		return <FontAwesomeIcon icon={column.sortBy === SortTypeEnum.descending ? faSortDown : faSortUp} key="sort" />;
	}
	function resizer() {
		if (!resizeable) return null;
		return (
			<div className={style.gridResize} data-id={column.id} onPointerDown={resizePointerDown}>
				&nbsp;
			</div>
		);
	}
}
